<template>
  <section>
    <div class="people-toolbar">
      <div>
        <h3>{{ $t('people_title') }}</h3>
        <span class="people-state" :class="stateClass">{{ stateLabel }}</span>
      </div>
      <div class="people-actions">
        <b-button
          v-if="status.jobs.failed"
          class="people-failures-button"
          size="sm"
          variant="outline-warning"
          @click="$emit('open-failures')"
        >
          <i class="bi bi-exclamation-triangle" />
          {{ status.jobs.failed }} {{ $t('people_failed_short') }}
        </b-button>
        <b-button v-if="status.jobs.total === 0" variant="primary" @click="$emit('analyze', 'all')">
          <i class="bi bi-stars" /> {{ $t('people_analyze_all') }}
        </b-button>
        <b-dropdown v-else right variant="outline-primary" :text="$t('people_analyze')">
          <b-dropdown-item @click="$emit('analyze', 'missing')">
            {{ $t('people_analyze_missing') }}
          </b-dropdown-item>
          <b-dropdown-item @click="$emit('analyze', 'all')">
            {{ $t('people_analyze_all') }}
          </b-dropdown-item>
          <b-dropdown-divider />
          <b-dropdown-item @click="$emit('rebuild')">{{ $t('people_rebuild') }}</b-dropdown-item>
          <b-dropdown-divider />
          <b-dropdown-item @click="$emit('disable')">{{ $t('people_disable') }}</b-dropdown-item>
          <b-dropdown-item variant="danger" @click="$emit('erase')">{{ $t('people_erase') }}</b-dropdown-item>
        </b-dropdown>
        <b-button variant="outline-light" @click="$emit('pause', !status.jobs.paused)">
          <i :class="status.jobs.paused ? 'bi bi-play-fill' : 'bi bi-pause-fill'" />
          {{ status.jobs.paused ? $t('people_resume') : $t('people_pause') }}
        </b-button>
        <b-button variant="outline-light" :disabled="!lastAction" @click="$emit('undo')">
          <i class="bi bi-arrow-counterclockwise" /> {{ $t('people_undo') }}
        </b-button>
      </div>
    </div>

    <div class="people-metrics">
      <button
        v-for="metric in metrics"
        :key="metric.filter"
        :class="{ active: activeFilter === metric.filter }"
        :aria-pressed="activeFilter === metric.filter ? 'true' : 'false'"
        @click="$emit('select-filter', metric.filter)"
      >
        <strong>{{ $t(metric.label) }}</strong>
        <span>{{ formatCount(metric.count) }} {{ $t(metric.unit) }}</span>
        <span>{{ formatCount(metric.photos) }} {{ $t('people_photos') }}</span>
      </button>
    </div>

    <div v-if="status.jobs.pending || status.jobs.running" class="people-progress">
      <b-progress :value="status.jobs.completed" :max="activeJobs" animated />
      <span>
        {{ status.jobs.completed }} {{ $t('people_completed') }} ·
        {{ status.jobs.pending }} {{ $t('people_queued') }} ·
        {{ status.jobs.running }} {{ $t('people_running') }}
      </span>
    </div>

    <div
      v-if="status.jobs.duration.estimated_remaining_ms != null && (status.jobs.pending || status.jobs.running)"
      class="people-diagnostics"
    >
      <span>
        <i class="bi bi-stopwatch" />
        {{ $t('people_estimated_remaining') }}:
        {{ formatDuration(status.jobs.duration.estimated_remaining_ms) }}
      </span>
    </div>

  </section>
</template>

<script>
import formatDuration from './formatDuration.js'

export default {
  props: {
    status: { type: Object, required: true },
    lastAction: { type: Object, default: null },
    activeFilter: { type: String, required: true },
  },
  computed: {
    activeJobs() {
      return Math.max(1, this.status.jobs.pending + this.status.jobs.running + this.status.jobs.completed)
    },
    metrics() {
      return [
        {
          filter: 'confirmed', label: 'people_confirmed', unit: 'people_people',
          count: this.status.people.confirmed, photos: this.status.people.confirmed_photos,
        },
        {
          filter: 'candidate', label: 'people_unconfirmed', unit: 'people_groups',
          count: this.status.people.candidates, photos: this.status.people.candidate_photos,
        },
        {
          filter: 'review', label: 'people_needs_review', unit: 'people_faces',
          count: this.status.people.needs_review, photos: this.status.people.needs_review_photos,
        },
        {
          filter: 'ungrouped', label: 'people_ungrouped', unit: 'people_faces',
          count: this.status.people.unassigned, photos: this.status.people.unassigned_photos,
        },
        {
          filter: 'hidden', label: 'people_hidden', unit: 'people_people',
          count: this.status.people.hidden, photos: this.status.people.hidden_photos,
        },
      ]
    },
    stateLabel() {
      if (this.status.jobs.paused) return this.$t('people_paused')
      if (this.status.jobs.pending || this.status.jobs.running) return this.$t('people_indexing')
      return this.$t('people_ready')
    },
    stateClass() {
      if (this.status.jobs.paused) return 'paused'
      if (this.status.jobs.pending || this.status.jobs.running) return 'indexing'
      return 'ready'
    },
  },
  methods: {
    formatDuration,
    formatCount(value) {
      return Number(value || 0).toLocaleString()
    },
  },
}
</script>

<style scoped>
.people-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.people-toolbar h3 {
  display: inline;
  margin-right: .7rem;
}

.people-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: .5rem;
}

.people-actions .btn:disabled {
  opacity: var(--disabled-opacity);
}

.people-state {
  padding: .25rem .65rem;
  border-radius: 99px;
  font-size: .75rem;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.people-state.ready {
  background: rgba(37, 134, 75, .15);
  color: #25864b;
}

.people-state.indexing {
  background: rgba(255, 171, 0, .17);
  color: #a46a00;
}

.people-state.paused,
.people-state.error {
  background: rgba(190, 50, 50, .15);
  color: #a12828;
}

.people-metrics {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: .5rem;
  margin-bottom: 1rem;
}

.people-metrics button {
  padding: 1rem;
  border: 1px solid rgba(127, 127, 127, .25);
  border-radius: 12px;
  background: rgba(127, 127, 127, .05);
  color: inherit;
  text-align: left;
  display: flex;
  min-height: 6rem;
  flex-direction: column;
  justify-content: center;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}

.people-metrics strong {
  display: block;
  font-size: 1.25rem;
}

.people-metrics span {
  display: block;
  color: var(--faded-text-color);
  font-size: .8rem;
  line-height: 1.35;
}

.people-metrics button:hover,
.people-metrics button:focus {
  border-color: var(--highlight-color);
  outline: none;
}

.people-metrics button.active {
  border-color: var(--highlight-color);
  background: var(--highlight-color);
  color: white;
}

.people-metrics button.active span {
  color: rgba(255, 255, 255, .78);
}

.people-progress {
  display: grid;
  gap: .4rem;
  margin: 1.25rem 0;
  color: var(--faded-text-color);
  font-size: .85rem;
}

.people-diagnostics {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem 1.5rem;
  margin: .75rem 0;
  color: var(--faded-text-color);
  font-size: .85rem;
}

@media (max-width: 800px) {
  .people-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .people-metrics {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
